import React from "react";
import { useNavigate } from "react-router-dom";
import Footer from "@/components/layout/Footer";

const POLICY_SECTIONS = [
  {
    title: "1. Information We Collect",
    body: [
      "Chattify is designed to work with minimal data. We do not collect or store personal details such as names, email addresses, phone numbers, passwords, payment information, or visitor credentials.",
      "The app may use limited site-level information only when needed for the features you enable, such as communication links, button labels, icon choices, placement preferences, display settings, and other app configuration values.",
    ],
  },
  {
    title: "2. How We Use Information",
    body: [
      "Any information used by Chattify is used only to provide the app experience selected by the site owner.",
      "This may include showing chat or contact buttons on your website, applying your saved settings, redirecting visitors to the contact channel you configured, and keeping the app functioning correctly inside the Wix platform.",
    ],
    listIntro: "Chattify does not:",
    list: [
      "sell data",
      "share data with advertisers",
      "track website visitors for marketing",
      "use app data for third-party analytics",
      "collect end-user conversations",
    ],
  },
  {
    title: "3. Data Storage",
    body: [
      "Chattify does not store personal user data on external servers. App configuration data may be stored within Wix infrastructure so the app can remember the settings chosen by the site owner.",
      "Wix platform storage and security practices apply to data handled inside the Wix ecosystem.",
    ],
  },
  {
    title: "4. Third-Party Services",
    body: [
      "Chattify may connect visitors to third-party services such as WhatsApp, Telegram, Instagram, Messenger, email, phone links, or other platforms through links configured by the site owner.",
      "These services are operated independently from Chattify. We do not control their data handling practices, so we recommend reviewing the privacy policies of any third-party services you choose to connect.",
    ],
  },
  {
    title: "5. Children's Privacy",
    body: [
      "Chattify is not directed to children under the age of 13. We do not knowingly collect personal information from children.",
    ],
  },
  {
    title: "6. Security",
    body: [
      "We take reasonable steps to keep Chattify privacy-friendly, limit data access, and follow applicable Wix App Market requirements. However, no internet-based service can be guaranteed to be completely secure.",
    ],
  },
  {
    title: "7. Changes to This Policy",
    body: [
      "We may update this Privacy Policy when our app, practices, or platform requirements change. When we make updates, the revised date on this page will be changed accordingly.",
    ],
  },
  {
    title: "8. Contact Us",
    body: [
      "If you have questions about this Privacy Policy or how Chattify handles data, please contact us through the support option provided on the relevant Wix App Market listing.",
    ],
  },
  {
    title: "9. Wix Platform Privacy",
    body: [
      "Chattify operates inside the Wix ecosystem. For details about how Wix handles platform data, please review Wix's own privacy policy.",
    ],
  },
];

const PrivacyPolicyPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-primary text-[#181818] min-h-screen antialiased overflow-x-hidden">
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-8 md:px-16 lg:px-24 py-6 bg-primary/80 backdrop-blur-md">
        <a
          href="/"
          className="text-2xl font-black uppercase tracking-[0.2em] transition-colors duration-300 text-[#181818] hover:text-[#6f685e]"
        >
          MA
        </a>

        <button
          onClick={() => navigate(-1)}
          className="group relative overflow-hidden text-xs uppercase tracking-widest text-black/50 flex items-center gap-2"
        >
          <span className="block transition-transform duration-300 group-hover:-translate-y-full text-[#181818]">
            Back
          </span>
          <span className="absolute inset-0 flex items-center text-[#181818]/80 translate-y-full transition-transform duration-300 group-hover:translate-y-0">
            Back
          </span>
        </button>
      </header>

      <main className="page-enter px-6 md:px-12 lg:px-24 pt-32 pb-24">
        <section className="mx-auto max-w-5xl">
          <p className="mb-5 text-xs uppercase tracking-[0.3em] text-black/45">
            Chattify
          </p>
          <h1
            className="text-[#181818] uppercase leading-none"
            style={{
              fontFamily: "'League Gothic', sans-serif",
              fontSize: "clamp(4rem, 13vw, 11rem)",
            }}
          >
            Privacy Policy
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-lg leading-8 text-black/65">
            Last updated: June 20, 2026
          </p>
          <p className="mt-8 max-w-3xl text-base md:text-lg leading-8 text-black/70">
            Chattify ("we", "our", or "us") respects your privacy. This Privacy
            Policy explains how we handle information when you install, access,
            or use Chattify apps and related features available through the Wix
            App Market.
          </p>
        </section>

        <section className="mx-auto mt-16 max-w-5xl border-t border-black/15">
          {POLICY_SECTIONS.map((section) => (
            <article
              key={section.title}
              className="grid gap-6 border-b border-black/15 py-10 md:grid-cols-[0.75fr_1.25fr]"
            >
              <h2
                className="text-3xl uppercase text-[#181818] md:text-4xl"
                style={{ fontFamily: "'League Gothic', sans-serif" }}
              >
                {section.title}
              </h2>
              <div className="space-y-5 text-sm leading-7 text-black/68 md:text-base md:leading-8">
                {section.body.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                {section.listIntro && <p>{section.listIntro}</p>}
                {section.list && (
                  <ul className="grid gap-3 text-black/75">
                    {section.list.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-3 h-px w-5 shrink-0 bg-black/40" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </article>
          ))}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
